import React from 'react';
import Chip from '@mui/material/Chip';

const formatYieldValue = (rawValue, unit) => {
  const raw = parseFloat(rawValue) || 0;
  const safeUnit = unit || 'g';
  if (!raw) return '';
  if (safeUnit === 'g') {
    return raw >= 1000 ? `${(raw / 1000).toFixed(2)} kg` : `${raw} g`;
  }
  if (safeUnit === 'oz') {
    return raw >= 16 ? `${(raw / 16).toFixed(2)} lb` : `${raw} oz`;
  }
  return `${raw} ${safeUnit}`;
};

export const createHarvestSchema = ({
  formatDate,
  variant = 'dashboard',
  getSeedDisplay,
  getTrayDisplay,
  getYieldDisplay,
  getPortionsLeft,
}) => {
  if (variant === 'full') {
    return {
      title: 'Harvest Details',
      summary: {
        title: 'Summary',
        fields: [
          {
            key: 'startDate',
            label: 'Start Date',
            format: (v) => (v ? <Chip label={formatDate(v)} size="small" variant="outlined" /> : ''),
          },
          {
            key: 'harvestDate',
            label: 'Harvest Date',
            format: (v) => (v ? <Chip label={formatDate(v)} size="small" variant="outlined" /> : ''),
          },
          {
            key: 'seeds',
            label: 'Seeds',
            format: (value) => {
              if (Array.isArray(value)) {
                return value
                  .map(
                    (seedEntry) =>
                      `${seedEntry.seedId ?? 'Unknown'} (${seedEntry.sowDensityValue ?? '-'} ${seedEntry.sowDensityUnit ?? '-'})`
                  )
                  .join(', ');
              }
              if (value && typeof value === 'object') {
                return `${value.seedId ?? 'Unknown'} (${value.sowDensityValue ?? '-'} ${value.sowDensityUnit ?? '-'})`;
              }
              return value ?? '';
            },
          },
          { key: 'trays', label: 'Trays' },
          {
            key: 'yieldValue',
            label: 'Yield per Tray',
            format: (_, row) => formatYieldValue(row?.yieldValue, row?.yieldUnit),
          },
          {
            key: 'totalYieldValue',
            label: 'Total Yield',
            format: (val, row) => formatYieldValue(val, row?.yieldUnit),
          },
          { key: 'portions', label: 'Portions' },
          { key: 'portionsLeft', label: 'Portions Left', format: (v) => (v != null ? String(v) : '') },
        ],
      },
      sections: [
        {
          title: 'Process',
          fields: [
            { key: 'soakHours', label: 'Soak Seeds', format: (v) => (v != null && v !== '' ? `${v} hour(s)` : '') },
            { key: 'germinationDays', label: 'Germinate Seeds', format: (v) => (v != null && v !== '' ? `${v} day(s)` : '') },
            { key: 'blackoutDays', label: 'Tray Blackout', format: (v) => (v != null && v !== '' ? `${v} day(s)` : '') },
            {
              key: 'wateringFrequency',
              label: 'Water Every',
              format: (v, row) =>
                v != null && v !== '' ? `${v} ${row.wateringIntervalUnit === 'weeks' ? 'week(s)' : 'day(s)'}` : '',
            },
            { key: 'growth', label: 'Growing Period', format: (v) => (v != null && v !== '' ? `${v} day(s)` : '') },
            { key: 'shelfLife', label: 'Shelf Life', format: (v) => (v != null && v !== '' ? `${v} day(s)` : '') },
            { key: 'harvestBuffer', label: 'Harvest Buffer', format: (v) => (v != null && v !== '' ? `${v} day(s)` : '') },
          ],
        },
        {
          title: 'Notes',
          fields: [{ key: 'notes', label: 'Notes' }],
        },
      ],
    };
  }

  return {
    title: 'Harvest Details',
    summary: {
      title: 'Summary',
      fields: [
        {
          key: 'harvestDate',
          label: 'Harvest Date',
          format: (v) => (v ? <Chip label={formatDate(v)} size="small" variant="outlined" /> : ''),
        },
        { key: 'seedDisplay', label: 'Seeds', getValue: (h) => getSeedDisplay(h) },
        { key: 'trayDisplay', label: 'Trays', getValue: (h) => getTrayDisplay(h) },
        { key: 'yield', label: 'Yield', getValue: (h) => getYieldDisplay(h) },
        { key: 'portionsLeft', label: 'Portions Left', getValue: (h) => getPortionsLeft(h) },
      ],
    },
    sections: [
      {
        title: 'Notes',
        fields: [{ key: 'notes', label: 'Notes' }],
      },
    ],
  };
};
